import React, { useEffect, useState, useRef } from "react";
import { Plus, Trash2, Upload, FileText, ShieldCheck, Users } from "lucide-react";
import api, { API } from "../../lib/api";
import { toast } from "sonner";

export default function EspaceAssureur() {
  const [products, setProducts] = useState([]);
  const [subs, setSubs] = useState([]);
  const [form, setForm] = useState({ name: "", type: "Habitation", description: "", base_price: "", coverage: "", cities: "National" });
  const [saving, setSaving] = useState(false);
  const [credit, setCredit] = useState(null);
  const fileRefs = useRef({});

  const load = () => {
    api.get("/insurance/my-products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/insurance/insurer-subscriptions").then((r) => setSubs(r.data)).catch(() => {});
    api.get("/insurance/my-credit").then((r) => setCredit(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const addProduct = async (e) => {
    e.preventDefault();
    if (!form.name || !form.base_price) { toast.error("Nom et prix de base requis"); return; }
    setSaving(true);
    try {
      const res = await api.post("/insurance/products", { ...form, base_price: Number(form.base_price) });
      setProducts([res.data, ...products]);
      setForm({ name: "", type: "Habitation", description: "", base_price: "", coverage: "", cities: "National" });
      toast.success("Produit publié");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } finally { setSaving(false); }
  };

  const del = async (id) => {
    await api.delete(`/insurance/products/${id}`);
    setProducts(products.filter((p) => p.id !== id));
    toast.success("Produit supprimé");
  };

  const uploadDoc = async (id, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post(`/insurance/products/${id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProducts(products.map((p) => p.id === id ? { ...p, docs: [...(p.docs || []), res.data] } : p));
      toast.success("Document PDF ajouté");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur d'upload"); }
  };

  return (
    <div className="space-y-6">
      {credit && (
        <div className="grid sm:grid-cols-3 gap-4" data-testid="insurer-credit">
          <div className="bg-white rounded-2xl border border-brand-soft p-5"><p className="text-xs text-brand-green/50">Souscriptions actives</p><p className="font-display text-2xl font-semibold text-brand-greenDeep">{credit.count}</p></div>
          <div className="bg-white rounded-2xl border border-brand-soft p-5"><p className="text-xs text-brand-green/50">Commission plateforme</p><p className="font-display text-2xl font-semibold text-orange-600">-{(credit.commission_total||0).toLocaleString("fr-FR")} €</p></div>
          <div className="bg-brand-greenDeep rounded-2xl p-5 text-white"><p className="text-xs text-white/60">Montant net crédité</p><p className="font-display text-2xl font-semibold" data-testid="net-credited">{(credit.net_credited||0).toLocaleString("fr-FR")} €</p></div>
        </div>
      )}
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-1">
        <div className="bg-white rounded-2xl p-6 border border-brand-soft sticky top-24">
          <h3 className="font-semibold text-lg text-brand-greenDeep mb-4 flex items-center gap-2"><Plus className="w-5 h-5" /> Nouveau produit</h3>
          <form onSubmit={addProduct} className="space-y-3">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nom du produit" className="input" />
            <input value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} placeholder="Type (Habitation, PNO, GLI...)" className="input" />
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description" rows={3} className="input" />
            <input type="number" value={form.base_price} onChange={(e) => setForm({ ...form, base_price: e.target.value })} placeholder="Prix de base annuel (€)" className="input" />
            <input value={form.coverage} onChange={(e) => setForm({ ...form, coverage: e.target.value })} placeholder="Couverture (ex: jusqu'à 30 000 €)" className="input" />
            <button type="submit" disabled={saving} className="w-full py-3 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold transition-colors disabled:opacity-60">{saving ? "..." : "Publier le produit"}</button>
          </form>
        </div>
      </div>

      <div className="lg:col-span-2 space-y-6">
        <div>
          <h3 className="font-semibold text-lg text-brand-greenDeep mb-4 flex items-center gap-2"><ShieldCheck className="w-5 h-5" /> Mes produits ({products.length})</h3>
          <div className="space-y-4">
            {products.map((p) => (
              <div key={p.id} className="bg-white rounded-2xl p-5 border border-brand-soft">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-semibold text-brand-greenDeep">{p.name} <span className="text-xs font-normal text-brand-green/50">· {p.type}</span></h4>
                    <p className="text-sm text-brand-green/60 mt-1">{p.description}</p>
                    <p className="text-sm text-brand-greenDeep font-medium mt-2">{p.base_price} €/an · {p.coverage}</p>
                  </div>
                  <button onClick={() => del(p.id)} className="text-red-400 hover:text-red-600 p-2"><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {(p.docs || []).map((d) => (
                    <a key={d.doc_id} href={`${API}/insurance/documents/${d.doc_id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-brand-softer text-brand-green hover:bg-brand-soft transition-colors"><FileText className="w-3.5 h-3.5" /> {d.filename?.slice(0, 20)}</a>
                  ))}
                  <input type="file" accept="application/pdf" ref={(el) => (fileRefs.current[p.id] = el)} onChange={(e) => uploadDoc(p.id, e.target.files[0])} className="hidden" />
                  <button onClick={() => fileRefs.current[p.id]?.click()} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-dashed border-brand-soft text-brand-green hover:bg-brand-softer transition-colors"><Upload className="w-3.5 h-3.5" /> Ajouter un PDF</button>
                </div>
              </div>
            ))}
            {products.length === 0 && <p className="text-brand-green/50 text-sm">Aucun produit publié pour le moment.</p>}
          </div>
        </div>

        <div>
          <h3 className="font-semibold text-lg text-brand-greenDeep mb-4 flex items-center gap-2"><Users className="w-5 h-5" /> Souscriptions reçues ({subs.length})</h3>
          <div className="bg-white rounded-2xl border border-brand-soft overflow-hidden">
            {subs.length === 0 ? (
              <p className="text-brand-green/50 text-sm p-6">Aucune souscription pour le moment.</p>
            ) : (
              <div className="divide-y divide-brand-soft">
                {subs.map((s) => (
                  <div key={s.id} className="flex items-center justify-between px-5 py-4">
                    <div>
                      <p className="font-medium text-brand-greenDeep text-sm">{s.product_name}</p>
                      <p className="text-xs text-brand-green/50">{s.property_type} · {s.surface} m² · {s.annual_amount} €/an</p>
                    </div>
                    <span className={`text-xs px-3 py-1 rounded-full font-semibold ${s.status === "Validée" ? "bg-brand-lime/30 text-brand-greenDeep" : "bg-orange-100 text-orange-700"}`}>{s.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <style>{`.input{width:100%;padding:0.7rem 1rem;border-radius:1rem;border:1px solid #efe7d6;outline:none;transition:all .2s}.input:focus{border-color:#c9a24b;box-shadow:0 0 0 3px rgba(201,162,75,.25)}`}</style>
    </div>
    </div>
  );
}
